import type { CookieOptions } from '#app'
import type { AuthCookieStore } from '@@/types/types'

export const useCookieStore = (
  options: CookieOptions & { readonly: false } = {
    path: '/',
    sameSite: 'lax',
    readonly: false,
  },
) => {
  const {
    public: { environment },
  } = useRuntimeConfig()
  const secureOptions: typeof options = {
    ...options,
    secure: ['production', 'staging'].includes(environment),
  }
  // auth (session cookies)
  const accessToken = useCookie<string | null>('access-token', secureOptions)
  const client = useCookie<string | null>('client', secureOptions)
  const uid = useCookie<string | null>('uid', secureOptions)
  // algolia
  const searchAdminToken = useCookie<string | null>(
    'search_admin_token',
    secureOptions,
  )
  const searchB2B2CToken = useCookie<string | null>(
    'search_b2b2c_token',
    secureOptions,
  )
  // wishlist
  const wishlistToken = useCookie<string | null>('user_wishlist_token')
  // hubspot
  const hubspotutk = useCookie<string | null>('hubspotutk')

  const cookieStore = {
    'access-token': accessToken,
    accessToken,
    client,
    hubspotutk,
    search_admin_token: searchAdminToken,
    search_b2b2c_token: searchB2B2CToken,
    searchAdminToken,
    searchB2B2CToken,
    uid,
    wishlistToken,
    user_wishlist_token: wishlistToken,
  } as const
  type CookieName = keyof typeof cookieStore

  const getCookie = <T extends CookieName = CookieName>(
    key: T,
  ): (typeof cookieStore)[typeof key]['value'] => cookieStore[key]?.value

  const setCookie = (
    key: CookieName,
    value: (typeof cookieStore)[typeof key]['value'] | null,
  ) => {
    if (key in cookieStore) {
      cookieStore[key].value = value
    }
  }
  const clearAllCookies = () => {
    for (const key in cookieStore) {
      setCookie(key as CookieName, null)
    }
  }
  const getAuthCookies: AuthCookieStore['getAuthCookies'] = <
    Mode = 'kebab' | 'camel',
  >(
    mode: Mode = 'kebab' as Mode,
  ) => {
    const tokenKey = mode === 'kebab' ? 'access-token' : 'accessToken'
    return ([tokenKey, 'client', 'uid'] as const).reduce(
      (acc, key) => {
        const value = getCookie(key)
        if (value && acc)
          return {
            ...acc,
            [key]: value,
          }
        return null
      },
      {} as ReturnType<AuthCookieStore['getAuthCookies']>,
    )
  }

  const setAuthCookies = (accessToken: string, client: string, uid: string) => {
    setCookie('access-token', accessToken)
    setCookie('client', client)
    setCookie('uid', uid)
  }

  return {
    clearAllCookies,
    getAuthCookies,
    getCookie,
    setAuthCookies,
    setCookie,
  }
}
